// Fachteil des Bildschirms "Testversand" (ST-BRV-001 Schnitt B2): Templates
// lesen und einen Test an die eigene Adresse ausloesen. Ohne React, mit
// austauschbarem `send` fuer die Proben.
//
// Die Saetze sagen, was geschah und was jetzt zu tun ist (NFR-UX-005); jeder
// Grundcode des Servers hat seinen eigenen, damit niemand "Fehler" liest, wo
// "zu viele Tests in dieser Stunde" gemeint ist.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import type { SendLike } from './parties.ts';

export interface BrevoTemplateView {
  readonly templateId: number;
  readonly name: string;
  readonly subject: string;
  readonly modifiedAt?: string;
}

interface ApiOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}

export type TemplatesResult =
  | { readonly outcome: 'loaded'; readonly templates: readonly BrevoTemplateView[]; readonly testRecipient?: string }
  /** Fuer dieses Haus ist kein Brevo-Konto eingerichtet oder der Weg ist abgeschaltet. */
  | { readonly outcome: 'unavailable'; readonly message: PlainMessage }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type TestSendResult =
  | { readonly outcome: 'sent'; readonly testRecipient: string }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

const OFFLINE: PlainMessage = {
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Die Verbindung kam nicht zustande.',
  nextStep: 'Versuche es erneut.',
  retryable: true,
};

/** Beim Senden ehrlich: die Anfrage kann angekommen sein, nur die Antwort nicht. */
export const SEND_OUTCOME_UNKNOWN: PlainMessage = {
  headline: 'Ob der Test versendet wurde, ist offen.',
  explanation: 'Die Verbindung brach ab, bevor LIAISON antworten konnte.',
  nextStep: 'Schau zuerst in dein Postfach. Fehlt der Test, sende ihn neu.',
  retryable: false,
};

/**
 * 202 ohne Testempfaenger: angenommen, aber ohne Beleg, wohin. Kein
 * "unterwegs" mit leerer Adresse; die Nutzerin prueft selbst.
 */
export const SEND_ACCEPTED_RECIPIENT_UNKNOWN: PlainMessage = {
  headline: 'Ob der Test angekommen ist, ist offen.',
  explanation: 'LIAISON hat den Test angenommen, aber nicht bestätigt, an welche Adresse er geht.',
  nextStep: 'Schau in dein Postfach. Fehlt der Test, sende ihn neu.',
  retryable: false,
};

export const NOT_SET_UP: PlainMessage = {
  headline: 'Der Testversand ist für deinen Verlag noch nicht eingerichtet.',
  explanation: 'Für deinen Verlag ist kein Brevo-Konto mit LIAISON verbunden.',
  nextStep: 'Wende dich an die Administration, wenn du Tests verschicken möchtest.',
  retryable: false,
};

/** Saetze je Grundcode. Unbekannte Codes fallen auf die allgemeine Uebersetzung zurueck. */
export const TEST_SEND_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  test_recipient_unavailable: {
    headline: 'Für deine Anmeldung ist keine Bonnier-Adresse bekannt.',
    explanation: 'Ein Test geht nur an deine eigene Bonnier-Adresse. Es wurde nichts versendet.',
    nextStep: 'Melde dich mit deinem Bonnier-Konto an oder wende dich an die Administration.',
    retryable: false,
  },
  client_request_reused: {
    headline: 'Dieser Test wurde schon mit einer anderen Vorlage ausgelöst.',
    explanation: 'Es wurde nichts weiter versendet.',
    nextStep: 'Wähle die Vorlage und sende den Test neu.',
    retryable: false,
  },
  test_send_outcome_pending: {
    headline: 'Ob dieser Test angekommen ist, ist noch offen.',
    explanation: 'Brevo hat nicht eindeutig geantwortet. LIAISON sendet denselben Test nicht noch einmal, damit er nicht doppelt ankommt.',
    nextStep: 'Schau in dein Postfach. Fehlt der Test, sende ihn neu.',
    retryable: false,
  },
  brevo_template_not_found: {
    headline: 'Diese Vorlage ist nicht mehr verfügbar.',
    explanation: 'Sie wurde in Brevo entfernt oder deaktiviert. Es wurde nichts versendet.',
    nextStep: 'Lade die Liste neu und wähle eine andere Vorlage.',
    retryable: false,
  },
  brevo_account_mismatch: {
    headline: 'Das verbundene Brevo-Konto gehört nicht zu deinem Verlag.',
    explanation: 'LIAISON hat den Versand deshalb gestoppt. Es wurde nichts versendet.',
    nextStep: 'Melde das der Administration; sie prüft die Einrichtung.',
    retryable: false,
  },
  brevo_account_unavailable: {
    headline: 'Das Brevo-Konto deines Verlags gibt keine Auskunft.',
    explanation: 'LIAISON konnte nicht prüfen, welches Konto verbunden ist. Es wurde nichts versendet.',
    nextStep: 'Melde das der Administration.',
    retryable: false,
  },
  brevo_unauthorized: {
    headline: 'Brevo hat den Zugang von LIAISON abgelehnt.',
    explanation: 'Der hinterlegte Schlüssel ist ungültig oder widerrufen. Es wurde nichts versendet.',
    nextStep: 'Melde das der Administration.',
    retryable: false,
  },
  interaction_context_ambiguous: {
    headline: 'Für welche Gesellschaft der Gruppe der Test gilt, ist nicht eindeutig.',
    explanation: 'Du arbeitest für mehrere Gesellschaften der Gruppe. Diese erste Fassung kann nur für eine einzige senden.',
    nextStep: 'Wende dich an die Administration, bis die Auswahl verfügbar ist.',
    retryable: false,
  },
  campaign_context_ambiguous: {
    headline: 'Für welche Gesellschaft der Gruppe der Test gilt, ist nicht eindeutig.',
    explanation: 'Du arbeitest für mehrere Gesellschaften der Gruppe. Diese erste Fassung kann nur für eine einzige senden.',
    nextStep: 'Wende dich an die Administration, bis die Auswahl verfügbar ist.',
    retryable: false,
  },
});

function rateLimited(retryAfter: string | null): PlainMessage {
  const seconds = Number(retryAfter);
  const minutes = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / 60) : undefined;
  return {
    headline: 'Du hast in dieser Stunde schon viele Tests verschickt.',
    explanation: 'Die Grenze schützt das Brevo-Kontingent deines Verlags. Es wurde nichts versendet.',
    nextStep: minutes === undefined ? 'Versuche es später erneut.' : `Versuche es in ${minutes === 1 ? 'einer Minute' : `${minutes} Minuten`} erneut.`,
    retryable: false,
  };
}

function messageFor(response: Response, payload: unknown): PlainMessage {
  if (response.status === 429) return rateLimited(response.headers.get('retry-after'));
  const reasonCode = reasonCodeOf(payload);
  return (reasonCode === undefined ? undefined : TEST_SEND_MESSAGES[reasonCode]) ?? plainMessageFor(reasonCode, factsOf(response, payload));
}

export async function fetchBrevoTemplates(options: ApiOptions): Promise<TemplatesResult> {
  const send = options.send ?? fetch;
  let response: Response;
  try {
    response = await send(`${options.baseUrl}/brevo-templates`, { headers: { authorization: `Bearer ${options.accessToken}` } });
  } catch {
    return { outcome: 'rejected', message: OFFLINE };
  }
  const payload = await response.json().catch(() => undefined);
  const reasonCode = reasonCodeOf(payload);
  if (reasonCode === 'brevo_not_configured' || reasonCode === 'brevo_connector_not_found') {
    return { outcome: 'unavailable', message: NOT_SET_UP };
  }
  if (!response.ok) return { outcome: 'rejected', message: messageFor(response, payload) };
  const body = (payload ?? {}) as Record<string, unknown>;
  const templates = Array.isArray(body.templates)
    ? (body.templates as Record<string, unknown>[]).flatMap((row): BrevoTemplateView[] =>
        typeof row.templateId === 'number' && typeof row.name === 'string'
          ? [
              {
                templateId: row.templateId,
                name: row.name,
                subject: typeof row.subject === 'string' ? row.subject : '',
                ...(typeof row.modifiedAt === 'string' ? { modifiedAt: row.modifiedAt } : {}),
              },
            ]
          : [],
      )
    : [];
  return {
    outcome: 'loaded',
    templates,
    ...(typeof body.testRecipient === 'string' ? { testRecipient: body.testRecipient } : {}),
  };
}

export async function sendBrevoTest(options: ApiOptions & { readonly templateId: number; readonly clientRequestId: string }): Promise<TestSendResult> {
  const send = options.send ?? fetch;
  let response: Response;
  try {
    response = await send(`${options.baseUrl}/brevo-test-sends`, {
      method: 'POST',
      headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
      // Die Bestaetigung je Versand: erst der Dialog setzt sie.
      body: JSON.stringify({ templateId: options.templateId, confirmed: true, clientRequestId: options.clientRequestId }),
    });
  } catch {
    return { outcome: 'rejected', message: SEND_OUTCOME_UNKNOWN };
  }
  const payload = await response.json().catch(() => undefined);
  if (response.status === 202) {
    const recipient = (payload as { testRecipient?: unknown } | undefined)?.testRecipient;
    return typeof recipient === 'string' && recipient.trim().length > 0
      ? { outcome: 'sent', testRecipient: recipient }
      : { outcome: 'rejected', message: SEND_ACCEPTED_RECIPIENT_UNKNOWN };
  }
  return { outcome: 'rejected', message: messageFor(response, payload) };
}

export function sentSentence(templateName: string, recipient: string): string {
  return `Der Test „${templateName}“ ist an ${recipient} unterwegs. Er kommt in der Regel binnen weniger Minuten an.`;
}
